<template>
    <div class="main-layout__container">
        <div class="content__header">
            <div class="content-header__title">Дилеры</div>

            <div class="content-header__actions">
                <router-link :to="{ name: 'AddDealer' }">
                    <UISimpleButton>
                        <template #icon>
                            <img src="@/assets/icons/ui/plus-white.svg" />
                        </template>
                        Добавить
                    </UISimpleButton>
                </router-link>
            </div>

            <div class="content-header__background content-header__background--2-lines-filters"></div>
        </div>

        <FilterPanel
            @search="onSearch"
            @reset="onReset"
        >
            <UIInput
                id="name"
                v-model="dealersFilter.name"
                label="Название"
            />

            <UIInput
                id="sap_code"
                v-model="dealersFilter.sap_code"
                label="Код"
            />

            <UISelect
                id="holding_id"
                v-model="dealersFilter.holding_id"
                :options="filterHoldingsOptions"
                label="Холдинг"
                multiple
                searchable
            />

            <UISelect
                id="brand_id"
                v-model="dealersBrandFilter"
                :options="filterBrandsOptions"
                label="Марка"
            />

            <UISelect
                id="status"
                v-model="dealersStatusFilter"
                :options="filterStatusesOptions"
                label="Статус"
            />
        </FilterPanel>

        <UISearchResults
            :count="dealersPaginationMeta?.pagination.total || 0"
            :is-visible-sort="false"
        />

        <UILoader v-if="isDealersLoading" class="tableable" />

        <div v-else class="content__body">
            <DataTable
                :headers="headers"
                :data="arDealers"
                :sort-key="sortKey"
                :sort-order="sortOrder"
                has-actions
                @sortable="changeSort"
                @row-click="openDealerCenterModal"
            >
                <template #actions="{ row }">
                    <UIDropDown as-modal-on-mobile>
                        <template #elements>
                            <router-link
                                :to="{ name: 'EditDealer', params: { dealer_id: row.id } }"
                                class="ui-dropdown-menu__element"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/edit.svg" />
                                <span>Редактировать</span>
                            </router-link>

                            <li
                                v-if="row.status === DealerStatus.Deactivated"
                                class="ui-dropdown-menu__element"
                                @click="handleActivateDealer(row as DealerPaginatorItem)"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/check.svg" />
                                <span>Активировать</span>
                            </li>

                            <li
                                v-if="row.status === DealerStatus.Active"
                                class="ui-dropdown-menu__element danger"
                                @click="handleDeactivateDealer(row as DealerPaginatorItem)"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/mingcute_close-circle-line.svg" />
                                <span>Деактивировать</span>
                            </li>
                        </template>
                    </UIDropDown>
                </template>

                <template #brand="{ row }">
                    <div v-if="row.is_hyundai_dealer">Hyundai</div>
                    <div v-if="row.is_genesis_dealer">Genesis</div>
                </template>

                <template #site="{ row }">
                    <a
                        v-if="row.site"
                        :href="row.site"
                        target="_blank"
                        @click.stop
                    >
                        <img v-svg-inline src="@/assets/icons/arrowGoLink.svg" />
                    </a>
                </template>

                <template #status="{ row }">
                    {{ getDealerStatusLabel(row.status) }}
                </template>
            </DataTable>
        </div>

        <div class="content__footer">
            <TablePagination
                :total-pages="dealersPaginationMeta?.pagination.last_page ?? 1"
                :current-page="dealersPaginationMeta?.pagination.current_page ?? 1"
                :items-per-page="dealersPaginationMeta?.pagination.per_page ?? 10"
                @page-change="changePage"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, h, inject, onMounted, ref } from 'vue';

    import { ModalPlugin } from '@/plugins/modalPlugin.ts';

    import { SelectOption } from '@/types/form.type.ts';
    import {
        DealerPaginatorItem,
        DealersRequestFilter,
        DealerBrand,
        DealerStatus,
        getDealerStatusLabel,
        getDealerStatusSelectOptions,
    } from '@/types/dealer.type.ts';
    import { Holding } from '@/types/holding.type.ts';

    import useDealerRepository from '@/repositories/dealer/useDealerRepository.ts';
    import useHoldingRepository from '@/repositories/holding/useHoldingRepository.ts';
    import useNotifications from '@/utils/useNotifications.ts';
    import { useDealersFilterStore } from '@/stores/dealers/dealers-filter.ts';

    import DataTable from '@/components/shared/DataTable/DataTable.vue';
    import FilterPanel from '@/components/shared/FilterPanel/FilterPanel.vue';
    import TablePagination from '@/components/shared/TablePagination/TablePagination.vue';

    import UIDropDown from '@/components/ui/UIDropDown/UIDropDown.vue';
    import UIInput from '@/components/ui/UIInput/UIInput.vue';
    import UILoader from '@/components/ui/UILoader/UILoader.vue';
    import UISearchResults from '@/components/ui/UISearchResults/UISearchResults.vue';
    import UISelect from '@/components/ui/UISelect/UISelect.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    import DealerModal from '@/modals/DealerModal/DealerModal.vue';

    import ConfirmActionModal from '@/components/modals/ConfirmActionModal/ConfirmActionModal.vue';

    import DealerConfirmActivate from '@/components/dealers/DealerConfirmActivate/DealerConfirmActivate.vue';
    import DealerConfirmDeactivate from '@/components/dealers/DealerConfirmDeactivate/DealerConfirmDeactivate.vue';

    const $modal = inject('$modal') as typeof ModalPlugin;

    const {
        arDealers,
        dealersPaginationMeta,
        isDealersLoading,
        getDealers,
        getDealerBrands,
        activateDealer,
        deactivateDealer,
    } = useDealerRepository();
    const { getAllActiveHoldings } = useHoldingRepository();
    const dealersFilterStore = useDealersFilterStore();
    const { showSuccess } = useNotifications();

    const dealersFilter = ref<DealersRequestFilter>({ ...dealersFilterStore.$state });
    const filterBrandsOptions = ref<SelectOption[]>([]);
    const filterHoldingsOptions = ref<SelectOption[]>([]);
    const filterStatusesOptions = ref(getDealerStatusSelectOptions());
    const headers = ref([
        { label: 'Марка', key: 'brand', sortable: false },
        { label: 'Холдинг', key: 'holding_name', sortable: true },
        { label: 'Название', key: 'name', sortable: true },
        { label: 'Юр. наименование', key: 'legal_entity_name', sortable: false },
        { label: 'Код', key: 'sap_code', sortable: false },
        { label: 'Сайт', key: 'site', sortable: false },
    ]);

    const sortKey = computed<string | undefined>(() => {
        if (dealersFilter.value.sort) {
            return Object.keys(dealersFilter.value.sort[0] ?? {})[0];
        }

        return undefined;
    });

    const sortOrder = computed<string | undefined>(() => {
        if (dealersFilter.value.sort) {
            return Object.values(dealersFilter.value.sort[0] ?? {})[0];
        }

        return undefined;
    });

    const dealersBrandFilter = computed<string | undefined>({
        get: () => {
            if (dealersFilter.value.is_hyundai_dealer) {
                return 'hyundai';
            }

            if (dealersFilter.value.is_genesis_dealer) {
                return 'genesis';
            }

            return '';
        },
        set: (brand?: string) => {
            dealersFilter.value.is_hyundai_dealer = brand === 'hyundai' ? 1 : undefined;
            dealersFilter.value.is_genesis_dealer = brand === 'genesis' ? 1 : undefined;
        },
    });

    const dealersStatusFilter = computed<DealerStatus | undefined>({
        get: () => {
            if (dealersFilter.value.status !== undefined && dealersFilter.value.status.length > 0) {
                return dealersFilter.value.status[0];
            }

            return undefined;
        },
        set: (status?: DealerStatus) => {
            dealersFilter.value.status = status !== undefined ? [status] : undefined;
        },
    });

    function onSearch(): void {
        dealersFilter.value.page = 1;
        fetchDealers();
    }

    function onReset(): void {
        dealersFilter.value = {
            page: 1,
            name: '',
            sap_code: '',
            holding_id: undefined,
            is_hyundai_dealer: undefined,
            is_genesis_dealer: undefined,
            sort: dealersFilter.value.sort,
            status: undefined,
        };
        fetchDealers();
    }

    function changePage(page: number): void {
        dealersFilter.value.page = page;
        fetchDealers();
    }

    function changeSort(key: string, dir: 'asc' | 'desc' | undefined): void {
        dealersFilter.value.sort = dir === undefined ? [{ name: 'asc' }] : [{ [key]: dir }];
        fetchDealers();
    }

    function openDealerCenterModal(dealer: DealerPaginatorItem): void {
        $modal.open({
            component: DealerModal,
            props: {
                dealerId: dealer.id,
            },
        });
    }

    function handleActivateDealer(dealer: DealerPaginatorItem): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    h(DealerConfirmActivate, { dealer }),
            },
            props: {
                onConfirm: async () => {
                    await activateDealer(dealer.id);

                    showSuccess({
                        title: 'Дилер активирован',
                    });

                    fetchDealers();
                },
            },
        });
    }

    function handleDeactivateDealer(dealer: DealerPaginatorItem): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    h(DealerConfirmDeactivate, { dealer }),
            },
            props: {
                onConfirm: async () => {
                    await deactivateDealer(dealer.id);

                    showSuccess({
                        title: 'Дилер деактивирован',
                    });

                    fetchDealers();
                },
                // showCancelButton: true, для примера, спросить у Алексея
            },
        });
    }

    function mapDealersFilterToRequestFilter(): DealersRequestFilter {
        const requestFilter: DealersRequestFilter = {
            page: dealersFilter.value.page,
            sort: dealersFilter.value.sort,
        };

        if (dealersFilter.value.name !== undefined && dealersFilter.value.name.length > 0) {
            requestFilter.name = dealersFilter.value.name;
        }

        if (dealersFilter.value.sap_code !== undefined && dealersFilter.value.sap_code.length > 0) {
            requestFilter.sap_code = dealersFilter.value.sap_code;
        }

        if (dealersFilter.value.holding_id !== undefined) {
            requestFilter.holding_id = dealersFilter.value.holding_id;
        }

        if (dealersFilter.value.is_hyundai_dealer !== undefined) {
            requestFilter.is_hyundai_dealer = 1;
        }

        if (dealersFilter.value.is_genesis_dealer !== undefined) {
            requestFilter.is_genesis_dealer = 1;
        }

        if (dealersFilter.value.status) {
            requestFilter.status = dealersFilter.value.status;
        }

        return requestFilter;
    }

    function fetchDealers(): void {
        dealersFilterStore.$patch({ ...dealersFilter.value });
        getDealers(mapDealersFilterToRequestFilter());
    }

    function loadFilterOptions(): void {
        getDealerBrands().then((brands: DealerBrand[]) => {
            const options: SelectOption[] = [];
            brands.forEach((brand: DealerBrand) => {
                options.push({ label: brand.name, value: brand.code });
            });
            filterBrandsOptions.value = options;
        });

        getAllActiveHoldings().then((holdings: Holding[]) => {
            const options: SelectOption[] = [];
            holdings.forEach((holding: Holding) => {
                options.push({ label: holding.name, value: holding.id });
            });
            filterHoldingsOptions.value = options;
        });
    }

    onMounted(() => {
        fetchDealers();
        loadFilterOptions();
    });
</script>
